import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Chip,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * DashboardMetricCard - A consistent, professional card component for dashboard metrics
 *
 * Features:
 * - Uniform height and layout
 * - Status indicator border
 * - Icon support
 * - Primary metric display
 * - Secondary metrics
 * - Expandable details
 * - Loading states
 */

const STATUS_COLORS = {
  'go': '#00E676',
  'advisory': '#FFEA00',
  'caution': '#FF9100',
  'no-go': '#FF5252',
  'unknown': '#999999'
};

const DashboardMetricCard = ({
  title,
  icon: Icon,
  status = 'unknown',
  primaryMetric,
  secondaryMetrics = [],
  statusMessage,
  loading = false,
  expanded = false,
  onToggleExpand,
  children,
  onClick,
  minHeight = 112
}) => {
  const borderColor = STATUS_COLORS[status] || STATUS_COLORS.unknown;
  const showChildren = Boolean(children) && (!onToggleExpand || expanded);

  return (
    <Card
      sx={{
        height: '100%',
        minHeight,
        display: 'flex',
        flexDirection: 'column',
        borderLeft: `4px solid ${borderColor}`,
        cursor: onClick || onToggleExpand ? 'pointer' : 'default',
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          boxShadow: onClick || onToggleExpand ? 4 : 1
        }
      }}
      onClick={onClick || onToggleExpand}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 1.1, height: '100%' }}>
        {/* Header Section */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.75}>
          <Box display="flex" alignItems="center" gap={1}>
            {Icon && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  backgroundColor: `${borderColor}20`,
                  color: borderColor
                }}
              >
                <Icon sx={{ fontSize: 18 }} />
              </Box>
            )}
            <Typography variant="subtitle1" component="div" sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          </Box>
          {onToggleExpand && (
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>

        {/* Loading State */}
        {loading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
              Loading...
            </Typography>
          </Box>
        )}

        {/* Content */}
        {!loading && (
          <>
            {/* Primary Metric */}
            {primaryMetric && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  component="div"
                  sx={{
                    fontWeight: 700,
                    color: borderColor,
                    mb: 0.25
                  }}
                >
                  {primaryMetric.value}
                </Typography>
                {primaryMetric.label && (
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    {primaryMetric.label}
                  </Typography>
                )}
              </Box>
            )}

            {/* Secondary Metrics */}
            {secondaryMetrics.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                {secondaryMetrics.map((metric, index) => (
                  <Chip
                    key={index}
                    label={`${metric.label}: ${metric.value}`}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderColor: metric.highlight ? borderColor : undefined,
                      backgroundColor: metric.highlight ? `${borderColor}10` : undefined
                    }}
                  />
                ))}
              </Box>
            )}

            {/* Status Message */}
            {statusMessage && (
              <Box
                sx={{
                  mt: 'auto',
                  pt: 1,
                  borderTop: '1px solid rgba(255,255,255,0.1)'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: borderColor
                  }}
                >
                  {statusMessage}
                </Typography>
              </Box>
            )}

            {/* Expandable Details */}
            {showChildren && (
              <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                {children}
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardMetricCard;
